// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://api.latlens.com';

async function safeJson(res: Response): Promise<any> {
  const text = await res.text();
  try { return JSON.parse(text); }
  catch {
    // Cloudflare SBFM / Access challenge returned HTML instead of JSON
    if (res.status === 403 || res.status === 503) {
      throw new Error('Request blocked by security filter. Please disable any VPN or browser extension and try again.');
    }
    throw new Error(`Unexpected server response (HTTP ${res.status}). Please try again.`);
  }
}

export interface AuthUser {
  userId: string;
  email: string;
  roles: string[];
  name?: string;
  plan?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface RegisterInput {
  email: string; password: string; fullName: string; turnstileToken: string;
  /** 'YYYY-MM-DD' */
  dateOfBirth: string;
  /** Only for someone under 18. */
  guardianEmail?: string;
}
/** Empty when signed in; guardianEmailMasked when an under-18 account is waiting for permission. */
export interface RegisterResult { guardianEmailMasked?: string }

interface AuthContextType extends AuthState {
  login: (email: string, password: string, turnstileToken: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'SET_USER'; payload: AuthUser | null }
  | { type: 'LOGOUT' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true };
    case 'SET_USER':
      return { user: action.payload, isAuthenticated: !!action.payload, isLoading: false };
    case 'LOGOUT':
      return { user: null, isAuthenticated: false, isLoading: false };
    default:
      return state;
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, { user: null, isAuthenticated: false, isLoading: true });

  const loadUser = async (throwOnFailure = false) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) { dispatch({ type: 'SET_USER', payload: null }); return; }

      const res = await fetch(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        localStorage.removeItem('auth_token');
        dispatch({ type: 'SET_USER', payload: null });
        if (throwOnFailure) throw new Error('Session verification failed. Please try again.');
        return;
      }
      const me = await safeJson(res) as { userId: string; email: string; roles: string[]; full_name?: string };
      dispatch({ type: 'SET_USER', payload: { userId: me.userId, email: me.email, roles: me.roles ?? [], name: me.full_name } });
    } catch (err) {
      dispatch({ type: 'SET_USER', payload: null });
      if (throwOnFailure) throw err;
    }
  };

  useEffect(() => { loadUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email: string, password: string, turnstileToken: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, turnstileToken }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.error === 'Invalid email or password' ? data.error : (data.error ?? 'Login failed'));
    localStorage.setItem('auth_token', data.access_token);
    await loadUser(true);
  };

  const register = async (input: RegisterInput): Promise<RegisterResult> => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: input.email, password: input.password, full_name: input.fullName, turnstileToken: input.turnstileToken,
        date_of_birth: input.dateOfBirth, guardian_email: input.guardianEmail,
      }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data.error === 'Email already registered' ? data.error : (data.error ?? 'Registration failed'));
    // Under 18: the account waits for a parent's or guardian's permission, so there's no session yet.
    if (data.status === 'guardian_consent_pending') return { guardianEmailMasked: data.guardian_email };
    localStorage.setItem('auth_token', data.access_token);
    await loadUser(true);
    return {};
  };

  const logout = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('auth_token');
    dispatch({ type: 'LOGOUT' });
  };

  const refreshUser = loadUser;

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
